import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bottles, Occasions, type Bottle, type Occasion } from '@/lib/api';
import { Button, Card, Icon, IconButton, Input, Modal, Pill, Skeleton, EmptyState, Badge, type IconName } from '@/design-system';
import { BottleCard, BottleCardSkeleton } from '@/components/BottleCard';
import { useToasts } from '@/lib/store';
import {
  OCCASION_PRESETS,
  FOOD_CATEGORY_LABELS,
  TASTE_LABELS,
  WINE_TYPE_LABELS,
  type OccasionCriteria,
  type OccasionPreset,
  type FoodCategory,
  type Taste,
  type WineType,
} from '@/lib/occasion-presets';

const MOOD_ICON: Record<string, IconName> = {
  bbq: 'flame',
  christmas: 'snow',
  tuesday: 'moon',
  anniversary: 'sparkles',
  party: 'party',
  fish: 'fish',
  game: 'beef',
  cheese: 'cookie',
  asian: 'cherry',
  default: 'utensils',
};

export function OccasionsPage() {
  const { t } = useTranslation();
  const [newOpen, setNewOpen] = useState(false);
  const occasionsQ = useQuery({ queryKey: ['occasions'], queryFn: () => Occasions.list() });

  return (
    <div className="grid gap-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">{t('occasions.title')}</h1>
          <p className="text-sm text-mist">{t('occasions.subtitle')}</p>
        </div>
        <Button variant="primary" iconLeft="plus" onClick={() => setNewOpen(true)}>
          {t('occasions.new')}
        </Button>
      </header>

      {occasionsQ.isLoading ? (
        <div className="grid gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={220} rounded="md" />
          ))}
        </div>
      ) : (occasionsQ.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon="utensils"
          title="Ingen anledninger enda"
          description="Velg en preset eller bygg din egen — vi finner flasker fra kjelleren og foreslår innkjøp fra katalogen."
          action={
            <Button variant="primary" iconLeft="plus" onClick={() => setNewOpen(true)}>
              {t('occasions.new')}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-8">
          {(occasionsQ.data ?? []).map((o) => (
            <OccasionLane key={o.id} occasion={o} />
          ))}
        </div>
      )}

      <NewOccasionModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}

function OccasionLane({ occasion }: { occasion: Occasion }) {
  const [editOpen, setEditOpen] = useState(false);
  const matchesQ = useQuery({
    queryKey: ['occasion.matches', occasion.id],
    queryFn: () => Occasions.matches(occasion.id),
  });
  const moodKey = (occasion.mood ?? 'default').toLowerCase();
  const icon = MOOD_ICON[moodKey] ?? MOOD_ICON.default ?? 'utensils';
  const criteria = (occasion.criteria as Partial<OccasionCriteria>) ?? {};
  const guests = criteria.guestCount;
  const bpg = criteria.bottlesPerGuest ?? 0.5;
  const bottlesNeeded = guests ? Math.ceil(guests * bpg) : null;
  const matches = matchesQ.data ?? [];
  const haveCount = matches.reduce((sum, b) => sum + b.qty, 0);
  const missingCount = bottlesNeeded ? Math.max(0, bottlesNeeded - haveCount) : 0;

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-candle/15 text-candle">
          <Icon name={icon} size={16} />
        </span>
        <div className="grid">
          <div className="flex items-center gap-2">
            <h2 className="font-serif text-2xl text-parchment">{occasion.name}</h2>
            {bottlesNeeded != null && (
              <Badge tone={missingCount > 0 ? 'ruby' : 'success'}>
                {haveCount} / {bottlesNeeded} flasker
              </Badge>
            )}
            {guests != null && (
              <Badge tone="mist">
                <Icon name="users" size={10} /> {guests}
              </Badge>
            )}
          </div>
          {occasion.description && <span className="text-xs text-mist line-clamp-1">{occasion.description}</span>}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <IconButton icon="edit" label="Rediger anledning" size="sm" onClick={() => setEditOpen(true)} />
          <Link
            to={`/occasions/${occasion.id}`}
            className="text-xs uppercase tracking-widest text-mist hover:text-candle"
          >
            Detaljer <Icon name="arrow-right" size={12} className="inline" />
          </Link>
        </div>
      </div>
      <EditOccasionModal open={editOpen} onClose={() => setEditOpen(false)} occasion={occasion} />

      {/* taste/wine type pills (criteria preview) */}
      {(criteria.wineTypes?.length || criteria.tastes?.length || criteria.foodCategories?.length) && (
        <div className="flex flex-wrap gap-1.5">
          {criteria.wineTypes?.slice(0, 3).map((w) => (
            <Pill key={w} variant="gold">{WINE_TYPE_LABELS[w]}</Pill>
          ))}
          {criteria.foodCategories?.slice(0, 3).map((f) => (
            <Pill key={f} icon={FOOD_CATEGORY_LABELS[f].icon}>{FOOD_CATEGORY_LABELS[f].label}</Pill>
          ))}
          {criteria.tastes?.slice(0, 3).map((t) => (
            <Pill key={t}>{TASTE_LABELS[t]}</Pill>
          ))}
        </div>
      )}

      {matchesQ.isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="w-56 flex-shrink-0">
              <BottleCardSkeleton />
            </div>
          ))}
        </div>
      ) : matches.length > 0 ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {matches.map((b: Bottle) => (
            <div key={b.id} className="w-56 flex-shrink-0">
              <BottleCard bottle={b} />
            </div>
          ))}
        </div>
      ) : (
        <Card className="p-6 text-sm text-mist">
          Ingen flasker fra kjelleren matcher denne anledningen enda.{' '}
          <Link to={`/occasions/${occasion.id}`} className="text-candle underline">
            Se forslag fra katalogen
          </Link>
        </Card>
      )}
    </motion.section>
  );
}

// ─── Wizard modal ─────────────────────────────────────────────────────

function NewOccasionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [step, setStep] = useState<'preset' | 'tune'>('preset');
  const [draft, setDraft] = useState<{ name: string; description: string; mood: string; criteria: OccasionCriteria }>({
    name: '',
    description: '',
    mood: '',
    criteria: {
      foodCategories: [],
      tastes: [],
      wineTypes: ['red'],
      bottlesPerGuest: 0.5,
      guestCount: 4,
    },
  });

  const create = useMutation({
    mutationFn: () =>
      Occasions.create({
        name: draft.name,
        description: draft.description || undefined,
        mood: draft.mood || undefined,
        criteria: draft.criteria as unknown as Record<string, unknown>,
      }),
    onSuccess: () => {
      push({ title: 'Anledning opprettet', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['occasions'] });
      reset();
      onClose();
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const reset = () => {
    setStep('preset');
    setDraft({
      name: '',
      description: '',
      mood: '',
      criteria: { foodCategories: [], tastes: [], wineTypes: ['red'], bottlesPerGuest: 0.5, guestCount: 4 },
    });
  };

  const choosePreset = (preset: OccasionPreset) => {
    setDraft({
      name: preset.name,
      description: preset.description,
      mood: preset.mood,
      criteria: structuredClone(preset.criteria),
    });
    setStep('tune');
  };

  const startBlank = () => {
    setDraft((d) => ({ ...d, name: '', description: '', mood: '' }));
    setStep('tune');
  };

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} title={step === 'preset' ? 'Velg type anledning' : 'Tilpass'} size="lg">
      {step === 'preset' ? (
        <div className="grid gap-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {OCCASION_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => choosePreset(p)}
                className="ui-card ui-card--interactive grid gap-2 p-4 text-left"
              >
                <div className="grid h-9 w-9 place-items-center rounded-full bg-candle/15 text-candle">
                  <Icon name={p.icon} size={16} />
                </div>
                <h4 className="font-serif text-lg text-parchment">{p.name}</h4>
                <p className="text-xs text-mist line-clamp-2">{p.description}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.criteria.wineTypes.slice(0, 2).map((w) => (
                    <Badge key={w} tone="gold">{WINE_TYPE_LABELS[w]}</Badge>
                  ))}
                </div>
              </button>
            ))}
          </div>
          <div className="ui-divider" />
          <button onClick={startBlank} className="text-sm text-mist hover:text-candle">
            Eller start på nytt fra blanke ark →
          </button>
        </div>
      ) : (
        <TuneStep
          draft={draft}
          setDraft={setDraft}
          onBack={() => setStep('preset')}
          onSubmit={() => create.mutate()}
          submitting={create.isPending}
        />
      )}
    </Modal>
  );
}

function EditOccasionModal({
  open,
  onClose,
  occasion,
}: {
  open: boolean;
  onClose: () => void;
  occasion: Occasion;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const c0 = (occasion.criteria as Partial<OccasionCriteria>) ?? {};
  const [draft, setDraft] = useState<{ name: string; description: string; mood: string; criteria: OccasionCriteria }>(() => ({
    name: occasion.name,
    description: occasion.description ?? '',
    mood: occasion.mood ?? '',
    criteria: {
      foodCategories: c0.foodCategories ?? [],
      tastes: c0.tastes ?? [],
      wineTypes: c0.wineTypes ?? ['red'],
      bottlesPerGuest: c0.bottlesPerGuest ?? 0.5,
      guestCount: c0.guestCount ?? 4,
      budgetPerBottleNok: c0.budgetPerBottleNok,
      notes: c0.notes,
    },
  }));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const update = useMutation({
    mutationFn: () =>
      Occasions.update(occasion.id, {
        name: draft.name,
        description: draft.description || undefined,
        mood: draft.mood || undefined,
        criteria: draft.criteria as unknown as Record<string, unknown>,
      }),
    onSuccess: () => {
      push({ title: 'Oppdatert', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['occasions'] });
      void qc.invalidateQueries({ queryKey: ['occasion.matches', occasion.id] });
      onClose();
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: () => Occasions.delete(occasion.id),
    onSuccess: () => {
      push({ title: 'Anledning slettet', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['occasions'] });
      onClose();
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  return (
    <Modal open={open} onClose={onClose} title={`Rediger ${occasion.name}`} size="lg">
      <div className="grid gap-4">
        <TuneStep
          draft={draft}
          setDraft={setDraft}
          onBack={onClose}
          onSubmit={() => update.mutate()}
          submitting={update.isPending}
          submitLabel="Lagre endringer"
          backLabel="Lukk"
        />
        {confirmDelete ? (
          <div className="rounded-md border border-[color:var(--ui-color-danger)]/40 bg-[color:var(--ui-color-danger)]/5 p-3 text-sm">
            <p className="text-parchment">Slette anledningen? Festede flasker blir ikke fjernet fra kjelleren.</p>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>Avbryt</Button>
              <Button variant="ruby" size="sm" iconLeft="trash" loading={remove.isPending} onClick={() => remove.mutate()}>
                Slett
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="self-start text-xs text-mist hover:text-[color:var(--ui-color-danger)]"
          >
            <Icon name="trash" size={12} className="inline" /> Slett anledning
          </button>
        )}
      </div>
    </Modal>
  );
}

function TuneStep({
  draft,
  setDraft,
  onBack,
  onSubmit,
  submitting,
  submitLabel = 'Opprett anledning',
  backLabel = 'Tilbake',
}: {
  draft: { name: string; description: string; mood: string; criteria: OccasionCriteria };
  setDraft: React.Dispatch<React.SetStateAction<{ name: string; description: string; mood: string; criteria: OccasionCriteria }>>;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel?: string;
  backLabel?: string;
}) {
  const c = draft.criteria;
  const toggle = <T,>(arr: T[], v: T): T[] => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="grid gap-5">
      <Input
        label="Navn"
        value={draft.name}
        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
        autoFocus
      />
      <Input
        label="Beskrivelse (valgfritt)"
        value={draft.description}
        onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
      />

      <Section title="Antall gjester" hint="Vi regner ut hvor mange flasker du trenger.">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                criteria: { ...d.criteria, guestCount: Math.max(1, (d.criteria.guestCount ?? 1) - 1) },
              }))
            }
            className="ui-btn ui-btn--icon"
            aria-label="Færre"
          >
            <Icon name="chevron-left" size={14} />
          </button>
          <div className="grid w-20 place-items-center">
            <span className="font-serif text-3xl text-candle">{c.guestCount ?? 4}</span>
            <span className="text-[10px] uppercase tracking-widest text-mist">gjester</span>
          </div>
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                criteria: { ...d.criteria, guestCount: Math.min(50, (d.criteria.guestCount ?? 1) + 1) },
              }))
            }
            className="ui-btn ui-btn--icon"
            aria-label="Flere"
          >
            <Icon name="chevron-right" size={14} />
          </button>
          <div className="ml-4">
            <label className="text-[10px] uppercase tracking-widest text-mist">Flasker per gjest</label>
            <select
              className="ui-input mt-1 w-auto"
              value={c.bottlesPerGuest}
              onChange={(e) =>
                setDraft((d) => ({ ...d, criteria: { ...d.criteria, bottlesPerGuest: Number(e.target.value) } }))
              }
            >
              <option value={0.3}>0.3 (lite vin)</option>
              <option value={0.5}>0.5 (standard)</option>
              <option value={0.6}>0.6 (raus)</option>
              <option value={0.75}>0.75 (festkveld)</option>
              <option value={1}>1.0 (sommelier-jobb)</option>
            </select>
          </div>
          <div className="ml-auto ui-card grid place-items-center px-4 py-2">
            <span className="text-[10px] uppercase tracking-widest text-mist">Trenger</span>
            <span className="font-serif text-2xl text-candle">
              ≈ {Math.ceil((c.guestCount ?? 4) * c.bottlesPerGuest)}
            </span>
          </div>
        </div>
      </Section>

      <Section title="Type vin">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(WINE_TYPE_LABELS) as WineType[]).map((w) => (
            <Pill
              key={w}
              active={c.wineTypes.includes(w)}
              onClick={() =>
                setDraft((d) => ({ ...d, criteria: { ...d.criteria, wineTypes: toggle(d.criteria.wineTypes, w) } }))
              }
              variant={c.wineTypes.includes(w) ? 'gold' : 'default'}
            >
              {WINE_TYPE_LABELS[w]}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Mat">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(FOOD_CATEGORY_LABELS) as FoodCategory[]).map((f) => {
            const meta = FOOD_CATEGORY_LABELS[f];
            return (
              <Pill
                key={f}
                icon={meta.icon}
                active={c.foodCategories.includes(f)}
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    criteria: { ...d.criteria, foodCategories: toggle(d.criteria.foodCategories, f) },
                  }))
                }
              >
                {meta.label}
              </Pill>
            );
          })}
        </div>
      </Section>

      <Section title="Smak / stil">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(TASTE_LABELS) as Taste[]).map((tk) => (
            <Pill
              key={tk}
              active={c.tastes.includes(tk)}
              onClick={() =>
                setDraft((d) => ({ ...d, criteria: { ...d.criteria, tastes: toggle(d.criteria.tastes, tk) } }))
              }
            >
              {TASTE_LABELS[tk]}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Budsjett (valgfritt)">
        <div className="flex items-center gap-3">
          <Input
            type="number"
            placeholder="200"
            value={c.budgetPerBottleNok ?? ''}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                criteria: {
                  ...d.criteria,
                  budgetPerBottleNok: e.target.value ? Number(e.target.value) : undefined,
                },
              }))
            }
            className="w-32"
          />
          <span className="text-xs text-mist">kr per flaske maks (katalogforslag)</span>
        </div>
      </Section>

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onBack} iconLeft="chevron-left">
          {backLabel}
        </Button>
        <Button
          variant="primary"
          iconLeft="check"
          loading={submitting}
          disabled={draft.name.trim().length < 2}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <div>
        <div className="text-xs uppercase tracking-widest text-mist">{title}</div>
        {hint && <div className="text-[10px] text-mist/70">{hint}</div>}
      </div>
      {children}
    </section>
  );
}
